import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import axios from 'axios';
import { useState, type ReactElement } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { z } from 'zod';

import { BrandPanel } from './_components/BrandPanel';
import { StepHeading } from './_components/StepHeading';
import { tokens } from '../../design/tokens';
import { Routes } from '../../routes/routes';
import { useAuthStore } from '../../stores';

/**
 * `ResetRequestPage` (AUT-110) — request a password-reset link.
 *
 * URL: `/auth/reset`. Anti-enum discipline (BR8): success state shows
 * for ANY 200 response from the backend; we never reveal whether the
 * email matched a real admin. Only 429 throttle surfaces an error so
 * the operator can wait the cooldown.
 */

const Schema = z.object({
  email: z.string().min(1, 'auth.login.email.error').email('auth.login.email.error'),
});

type FormValues = z.infer<typeof Schema>;

const parseRetryAfterMinutes = (err: unknown): number | undefined => {
  if (!axios.isAxiosError(err) || !err.response) return undefined;
  const data = err.response.data as { details?: { retryAfterSeconds?: number } } | undefined;
  const seconds = data?.details?.retryAfterSeconds;
  if (typeof seconds !== 'number') return undefined;
  return Math.max(1, Math.ceil(seconds / 60));
};

const RequestForm = ({
  onSuccess,
  onThrottle,
}: {
  onSuccess: () => void;
  onThrottle: (minutes: number | undefined) => void;
}): ReactElement => {
  const { t } = useTranslation();
  const authStore = useAuthStore();
  const [submitting, setSubmitting] = useState(false);
  const { control, handleSubmit } = useForm<FormValues>({
    resolver: zodResolver(Schema),
    defaultValues: { email: '' },
    mode: 'onSubmit',
  });

  const onSubmit = async (values: FormValues): Promise<void> => {
    setSubmitting(true);
    try {
      await authStore.requestPasswordReset(values.email);
      onSuccess();
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 429) {
        onThrottle(parseRetryAfterMinutes(err));
        return;
      }
      // Any other failure: still treat as anti-enum success so we don't
      // leak admin existence. Logged in DEV via the axios interceptor.
      onSuccess();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <StepHeading
        eyebrow={t('auth.login.step.eyebrow')}
        title={t('auth.reset.request.title')}
        subtitle={t('auth.reset.request.subtitle')}
      />
      <Controller
        name="email"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            fullWidth
            type="email"
            autoComplete="username"
            label={t('auth.reset.request.emailLabel')}
            placeholder={t('auth.reset.request.emailPlaceholder')}
            disabled={submitting}
            error={Boolean(fieldState.error)}
            inputProps={{
              'aria-label': t('auth.reset.request.emailLabel'),
              'data-testid': 'reset-request-email-input',
            }}
            sx={{ mb: '16px' }}
          />
        )}
      />
      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={submitting}
        sx={{ height: 52, borderRadius: '14px' }}
        data-testid="reset-request-submit"
      >
        {t('auth.reset.request.cta')}
      </Button>
      <Box sx={{ mt: '16px', textAlign: 'center' }}>
        <Link component={RouterLink} to={Routes.Auth.Login} underline="hover" sx={{ fontSize: 13 }}>
          {t('auth.reset.request.backToLogin')}
        </Link>
      </Box>
    </Box>
  );
};

type SuccessNoticeProps = {
  throttleMinutes: number | null;
};

const SuccessNotice = ({ throttleMinutes }: SuccessNoticeProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box data-testid="reset-request-success">
      <StepHeading
        eyebrow={t('auth.login.step.eyebrow')}
        title={t('auth.reset.request.successTitle')}
        subtitle={t('auth.reset.request.successBody')}
      />
      {throttleMinutes !== null && (
        <Alert severity="warning" sx={{ mb: '16px' }} data-testid="reset-throttle">
          {t('auth.reset.request.throttled', { minutes: throttleMinutes })}
        </Alert>
      )}
      <Typography sx={{ textAlign: 'center', mt: '8px' }}>
        <Link component={RouterLink} to={Routes.Auth.Login} underline="hover" sx={{ fontSize: 13 }}>
          {t('auth.reset.request.backToLogin')}
        </Link>
      </Typography>
    </Box>
  );
};

export const ResetRequestPage = (): ReactElement => {
  const [done, setDone] = useState(false);
  const [throttleMinutes, setThrottleMinutes] = useState<number | null>(null);
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: tokens.surface.base }}>
      <BrandPanel step="login" email="" />
      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '56px',
        }}
      >
        <Paper elevation={0} sx={{ width: '100%', maxWidth: 420, background: 'transparent' }}>
          {done ? (
            <SuccessNotice throttleMinutes={throttleMinutes} />
          ) : (
            <RequestForm
              onSuccess={() => {
                setThrottleMinutes(null);
                setDone(true);
              }}
              onThrottle={(minutes) => {
                setThrottleMinutes(minutes ?? 60);
                setDone(true);
              }}
            />
          )}
        </Paper>
      </Box>
    </Box>
  );
};
